import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { Board, UUID } from "@/lib/types";

interface Vars {
  workspaceId: UUID;
  // The full visible list in its NEW order — we re-index every board so the
  // intended order is fully captured (see reindexPositions).
  orders: { board_id: UUID; position: number }[];
}

// F1: persist the user's custom board order. Optimistically writes the new
// positions into the workspace + my-boards caches so the list doesn't snap back.
export function useReorderBoards() {
  const qc = useQueryClient();
  return useMutation<void, Error, Vars>({
    mutationFn: ({ orders }) => api.reorderBoards(orders),
    onMutate: ({ workspaceId, orders }) => {
      const posOf = new Map(orders.map((o) => [o.board_id, o.position]));
      const patch = (list: Board[] | undefined) =>
        list?.map((b) => (posOf.has(b.id) ? { ...b, order_position: posOf.get(b.id)! } : b));
      qc.setQueryData<Board[]>(queryKeys.boards(workspaceId), patch);
      qc.setQueryData<Board[]>(queryKeys.myBoards, patch);
    },
    onSettled: (_d, _e, { workspaceId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.boards(workspaceId) });
      void qc.invalidateQueries({ queryKey: queryKeys.myBoards });
    },
  });
}
